import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ViewIcon } from '../../assets/icons/background-eye-icon.svg';
import { ReactComponent as PdfIcon } from '../../assets/icons/export-pdf.svg';
import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { InvoiceInterface, RequestInvoiceInterface } from '../../interface/invoice-interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchInvoiceList,
  fetchInvoiceFilters,
  setSortData,
  updateInvoiceListFilter,
} from '../../redux/slices/InvoiceSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import CustomDateRangePicker from '../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import SidePaneModel from '../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { convertUtcTo12HourTime, removeEmptyKeys } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatTimeDuration from '../../shared-functions/FormatTimeDuration';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import invoiceApi from '../../utils/api/invoice';

import InvoiceSidePanel from './components/InvoiceSidePanel';
import downloadSingleInvoicePdf from './utils/generateInvoicePdf';

/**
 * Field mapping for sorting nested fields in EMSP invoices
 */
const sortFieldMap: Record<string, string> = {
  energyUsed: 'energyUsed',
  invoiceAmountPaid: 'invoiceAmountPaid',
};

/**
 * Properties for EmspInvoice component
 */
interface EmspInvoiceProperties {
  userCustomId?: string;
  isComponent?: boolean;
}

/**
 * Component to display the invoice list for EMSP
 * @param {EmspInvoiceProperties} [properties] properties
 * @returns {any} The result
 */
function EmspInvoice(properties: EmspInvoiceProperties = {}) {
  const { userCustomId, isComponent } = properties;
  const { t, i18n } = useTranslation('invoice');
  const dispatch = useAppDispatch();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceInterface | null>(null);
  const [isInvoicePanelOpen, setIsInvoicePanelOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  const {
    invoices,
    loading,
    page,
    limit,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortField,
    sortBy,
    isFilterUpdated,
  } = useAppSelector((state) => state.Invoice);

  /**
   * Fetch Invoice List
   * @param {RequestInvoiceInterface} data data
   */
  const fetchData = (data: RequestInvoiceInterface) => {
    if (!loading) {
      const payload: any = { limit: 20, ...data };
      if (userCustomId) {
        payload.userCustomId = userCustomId;
      }
      if (dateRange.startDate && payload.startDate === undefined) {
        payload.startDate = dateRange.startDate;
      }
      if (dateRange.endDate && payload.endDate === undefined) {
        payload.endDate = dateRange.endDate;
      }

      if (payload.sortField && sortFieldMap[payload.sortField]) {
        payload.sortField = sortFieldMap[payload.sortField];
      }

      dispatch(fetchInvoiceList(removeEmptyKeys(payload)));
    }
  };

  /**
   * Table Columns
   */
  const columns = [
    {
      label: t('invoiceNumber'),
      accessorKey: 'invoiceNumber' as keyof InvoiceInterface,
      sortAllow: false,
    },
    {
      label: t('invoiceDate'),
      accessorKey: 'invoiceDate' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for invoiceDate
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <span>{ConvertUTCtoDateTime(currentRow.invoiceDate)}</span>
      ),
    },
    {
      label: t('customerName'),
      accessorKey: 'invoiceTo' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for customerName
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => <p>{currentRow.invoiceTo?.name || '-'}</p>,
    },
    {
      label: t('userId'),
      accessorKey: 'userCustomId' as keyof InvoiceInterface,
      sortAllow: false,
    },
    {
      label: t('sessionId'),
      accessorKey: 'sessionId' as keyof InvoiceInterface,
      sortAllow: false,
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof InvoiceInterface,
      sortAllow: false,
    },
    {
      label: t('locationID'),
      accessorKey: 'chargerInfo' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for stationId
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>{currentRow.chargerInfo?.stationId || '-'}</p>
      ),
    },
    {
      label: t('chargerId'),
      accessorKey: 'chargerId' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for chargerId
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>{currentRow.chargerInfo?.chargerId || '-'}</p>
      ),
    },
    {
      label: t('energyUsed'),
      accessorKey: 'energyUsed' as keyof InvoiceInterface,
      sortAllow: true,
      /**
       * Custom Data for energyUsed
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>{`${Number(currentRow.chargingDetails?.energyUsed || 0).toFixed(3)} kWh`}</p>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'chargingDuration' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for duration
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => {
        return <p>{FormatTimeDuration(currentRow.chargingDetails?.chargingDuration)}</p>;
      },
    },
    {
      label: t('amount'),
      accessorKey: 'invoiceAmountPaid' as keyof InvoiceInterface,
      sortAllow: true,
      /**
       * Custom Data for amount
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <p>
          {ToFixedValue(currentRow.chargingDetails?.invoiceAmountPaid || 0)} {currentRow.currency}
        </p>
      ),
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for createdAt
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <span>{ConvertUTCtoDateTime(currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt' as keyof InvoiceInterface,
      sortAllow: false,
      /**
       * Custom Data for updatedAt
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <span>{ConvertUTCtoDateTime(currentRow.updatedAt)}</span>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof InvoiceInterface,
      sortAllow: false,
      ColumnWidth: 160,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Custom Data for action
       * @param {InvoiceInterface} currentRow currentRow
       * @returns {any} HTML data
       */
      customData: (currentRow: InvoiceInterface) => (
        <div className="table-action-icons">
          <button
            type="button"
            onClick={() => {
              setSelectedInvoice(currentRow);
              setIsInvoicePanelOpen(true);
            }}
            className="table-icon-button eye-icon-wrapper"
            title={t('invoiceDetails', { defaultValue: 'Invoice Details' })}
            aria-label={t('invoiceDetails', { defaultValue: 'Invoice Details' })}>
            <ViewIcon />
          </button>

          <button
            type="button"
            onClick={async () => {
              const success = await downloadSingleInvoicePdf(currentRow, i18n.language);
              if (!success) {
                dispatch(
                  setNotificationMessage({
                    message: t('somethingWentWrong', { defaultValue: 'Failed to generate PDF' }),
                    status: true,
                    type: 'error',
                    code: 500,
                  }),
                );
              }
            }}
            className="table-icon-button pdf-icon-wrapper"
            title={t('downloadPdf', { defaultValue: 'Export PDF' })}
            aria-label={t('downloadPdf', { defaultValue: 'Export PDF' })}>
            <PdfIcon />
          </button>
        </div>
      ),
    },
  ];

  /**
   * Current Columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('invoiceNumber'), accessorKey: 'invoiceNumber' },
    { label: t('invoiceDate'), accessorKey: 'invoiceDate' },
    { label: t('customerName'), accessorKey: 'customerName' },
    { label: t('userId'), accessorKey: 'userCustomId' },
    { label: t('sessionId'), accessorKey: 'sessionId' },
    { label: t('transactionId'), accessorKey: 'transactionId' },
    { label: t('locationID'), accessorKey: 'stationId' },
    { label: t('chargerId'), accessorKey: 'chargerId' },
    { label: t('energyUsed'), accessorKey: 'energyUsed' },
    { label: t('duration'), accessorKey: 'chargingDuration' },
    { label: t('amount'), accessorKey: 'invoiceAmountPaid' },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Builds export rows from invoice data
   * @param {InvoiceInterface[]} dataList - The invoice list
   * @returns {any[][]} The mapped row data
   */
  const buildExportRows = (dataList: InvoiceInterface[]): any[][] => {
    return dataList.map((row: InvoiceInterface, index: number) =>
      exportCurrentColumns.map((column) => {
        switch (column.accessorKey) {
          case '__serial__':
            return index + 1;

          case 'invoiceNumber':
            return row.invoiceNumber || '-';

          case 'invoiceDate':
            return row.invoiceDate
              ? `${ConvertUTCtoDate(row.invoiceDate)} ${convertUtcTo12HourTime(row.invoiceDate)}`
              : '-';

          case 'customerName':
            return row.invoiceTo?.name || '-';

          case 'userCustomId':
            return row.userCustomId || '-';

          case 'sessionId':
            return row.sessionId || '-';

          case 'transactionId':
            return row.transactionId || '-';

          case 'stationId':
            return row.chargerInfo?.stationId || '-';

          case 'chargerId':
            return row.chargerInfo?.chargerId || '-';

          case 'energyUsed':
            return row.chargingDetails?.energyUsed !== undefined &&
              row.chargingDetails?.energyUsed !== null
              ? Number(row.chargingDetails.energyUsed).toFixed(3)
              : '-';

          case 'chargingDuration':
            return FormatTimeDuration(row.chargingDetails?.chargingDuration);

          case 'invoiceAmountPaid':
            return row.chargingDetails?.invoiceAmountPaid !== undefined &&
              row.chargingDetails?.invoiceAmountPaid !== null
              ? `${ToFixedValue(row.chargingDetails.invoiceAmountPaid)} ${row.currency || ''}`
              : '-';

          case 'currency':
            return row.currency || '-';

          case 'createdAt':
            return row.createdAt
              ? `${ConvertUTCtoDate(row.createdAt)} ${convertUtcTo12HourTime(row.createdAt)}`
              : '-';

          case 'updatedAt':
            return row.updatedAt
              ? `${ConvertUTCtoDate(row.updatedAt)} ${convertUtcTo12HourTime(row.updatedAt)}`
              : '-';

          default:
            return '-';
        }
      }),
    );
  };

  /**
   * Export data function
   * @param {string} type - The export format type ('pdf' or 'csv').
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue =
      sortField && sortBy ? { sortField: sortFieldMap[sortField] || sortField, sortBy } : {};

    const payload: any = {
      ...queryParameters,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
      ...sortValue,
    };

    if (userCustomId) payload.userCustomId = userCustomId;

    invoiceApi
      .invoiceExport(removeEmptyKeys(payload))
      .then((response) => {
        const headers = exportCurrentColumns.map((column) => column.label);
        const rawList: InvoiceInterface[] = Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || response?.data?.data || [];

        const dataToExport = rawList.length > 0 ? rawList : invoices;
        const rows = buildExportRows(dataToExport);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'invoice-list.csv');
          } else {
            exportPDF(headers, rows, 'invoice-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        // Fallback to exporting currently loaded table invoices if API fails
        if (invoices.length > 0) {
          const headers = exportCurrentColumns.map((column) => column.label);
          const rows = buildExportRows(invoices);

          if (rows.length > 0) {
            if (type === 'csv') {
              exportCSV(headers, rows, 'invoice-list.csv');
            } else {
              exportPDF(headers, rows, 'invoice-list.pdf');
            }
          }
          setExportLoader(false);
          return;
        }

        setExportLoader(false);

        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message ||
              t('somethingWentWrong', { defaultValue: 'Something went wrong!' }),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 500,
          }),
        );
      });
  };

  /**
   * Pagination
   * @param {any} value page/limit
   */
  const pageLimitChange = (value: any) => {
    const parameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};

    fetchData({
      ...parameters,
      ...value,
      ...sortValue,
    });
  };

  /**
   * Sorting
   * @param {any} sortData sort options
   */
  const handleSort = (sortData: any) => {
    dispatch(setSortData(sortData));

    const parameters = GetParametersFromCommonFilters(filters);

    fetchData({
      page: DefaultApiLimit.page,
      limit,
      ...parameters,
      sortField: sortData.sortField,
      sortBy: sortData.sortBy,
    });
  };

  /**
   * Refresh
   */
  const handleRefresh = () => {
    const parameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};

    fetchData({
      page,
      limit,
      ...parameters,
      ...sortValue,
    });
  };

  useEffect(() => {
    if (userCustomId) {
      const currentId = userCustomId;
      const lastId = sessionStorage.getItem('invoicesEndConsumer');

      if (isFilterUpdated || invoices.length === 0 || lastId !== currentId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit: 20,
          ...queryParameters,
        });
      }
      sessionStorage.setItem('invoicesEndConsumer', currentId);
    } else if (isFilterUpdated || invoices.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit: 20,
        ...queryParameters,
      });
    }
  }, [filters, isFilterUpdated, userCustomId]);

  useEffect(() => {
    if (!isComponent) {
      dispatch(
        setPageTitle({ title: t('invoice'), icon: <TransactionIcon />, count: totalResults }),
      );
    }
  }, [totalResults, t, isComponent, dispatch]);

  return (
    <div className="invoice-list-wrap" dir={i18n.dir()}>
      <CustomTable
        dataKey="InvoiceManagement"
        header={columns}
        data={invoices}
        serialNo
        actionButton
        sizeChangeButtonRequired
        isExportRequired
        version="v2"
        height={isComponent ? 500 : undefined}
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortField, sortBy }}
        handleSortChange={handleSort}
        pageLimitChange={pageLimitChange}
        filters={filters}
        filtersToDispatch={updateInvoiceListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchInvoiceFilters}
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        rightTopContent={
          <div>
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => {
                setDateRange({ startDate: start, endDate: end });
                const queryParameters = GetParametersFromCommonFilters(filters);
                const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
                fetchData({
                  page: DefaultApiLimit.page,
                  limit,
                  ...queryParameters,
                  startDate: start,
                  endDate: end,
                  ...sortValue,
                });
              }}
              placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
              defaultPreset="last14Days"
              disableDates="future"
            />
          </div>
        }
        leftTopContent={<> </>}
        expandTableOpenKey="InvoiceList"
        exportData={invoices}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        onHiddenColumnsChange={setHiddenColumns}
      />

      <SidePaneModel
        open={isInvoicePanelOpen && Boolean(selectedInvoice)}
        title={t('invoiceDetails', 'Invoice Details')}
        width="42vw"
        closePanel={() => {
          setIsInvoicePanelOpen(false);
          setSelectedInvoice(null);
        }}
        content={
          selectedInvoice ? (
            <InvoiceSidePanel
              invoice={selectedInvoice}
              onClose={() => {
                setIsInvoicePanelOpen(false);
                setSelectedInvoice(null);
              }}
            />
          ) : (
            <div />
          )
        }
      />
    </div>
  );
}

export default EmspInvoice;
